import { Box, Card, CardBody } from '@chakra-ui/react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { ResourceType } from '../../_openapi/portfolio-optimizer';
import { useRestrictions } from '../../api/restrictions';
import { restrictionListColumns } from '../../Restrictions/restrictionListColumns';
import { useRestrictionRowActions } from '../../Restrictions/useRestrictionRowActions';
import { AsOfDateTag } from '../../widgets/Table/AsOfDateTag';
import { Pagination } from '../../widgets/Table/Pagination';
import { Table } from '../../widgets/Table/Table';
import { TableControls } from '../../widgets/Table/TableControls';
import { useTableColumnSettings } from '../../widgets/Table/useTableColumnSettings';
import { useTableState } from '../../widgets/Table/useTableState';

const EXCLUDE_COLUMNS = ['pId', 'aC', 'portfolioName', 'householdName', 'portfolioGuid'];
const dataColumns = restrictionListColumns.filter((col) => col.id && !EXCLUDE_COLUMNS.includes(col.id));

export const PortfolioRestrictionsTableTab = ({ pId }: { pId: string }) => {
    const { restrictionActionColumn, drawers } = useRestrictionRowActions();

    const { pagination, setPagination, sorting, setSorting, columnFilters, setColumnFilters } = useTableState({
        columns: dataColumns,
        initialFilters: [{ id: 'pId', value: pId }],
    });

    const { data: restrictionPage, isFetching } = useRestrictions({ pagination, columnFilters, sorting });

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } =
        useTableColumnSettings('portfolioRestrictions');

    const resetFilters = useCallback(() => {
        setColumnFilters([]);
        setPagination((current) => ({ ...current, pageIndex: 0 }));
    }, [setColumnFilters, setPagination]);

    const columns = useMemo(() => [restrictionActionColumn, ...dataColumns], [restrictionActionColumn]);

    const table = useReactTable({
        data: restrictionPage?.restrictions || [],
        columns: columns,
        state: {
            columnVisibility,
            columnOrder,
            pagination,
            sorting,
            columnFilters,
        },
        initialState: {
            columnPinning: {
                left: ['contextMenu'],
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <Box>
            <Card>
                <CardBody>
                    <TableControls table={table} onReset={resetFilters} resourceType={ResourceType.RESTRICTION}>
                        <AsOfDateTag dateString={restrictionPage?.asOfDate} />
                    </TableControls>
                    <Table table={table} isLoading={isFetching} allowXOverflow hiddenFilterIds={['pId']} />
                    <Pagination table={table} totalItems={restrictionPage?.totalNr} />
                </CardBody>
            </Card>
            {drawers}
        </Box>
    );
};
